import { useEffect, useState, useCallback } from "react";

import api from "../../../../Services/Api/api";
import { getFullName } from "../../../../Modules/utils/fullname";

/* ---------------- TrCard (unchanged) ---------------- */

const TrCard = ({ data, style }) => {
  const { title, pickup, delivery, cols } = data;

  return (
    <div className="card mb-2" style={{ fontSize: 12, ...style }}>
      <div className="card-body p-2">
        <div className="row mb-1">
          <div className="col-6">
            <div className="me-2 fw-semibold">{title}</div>
          </div>
          <div className="col-3">
            <span className="small text-muted">Pick </span>
            <span className="small">{pickup}</span>
          </div>
          <div className="col-3">
            <span className="small text-muted">Delv </span>
            <span className="small">{delivery}</span>
          </div>
        </div>

        <div className="row gx-0 gy-0">
          {cols.map(({ title, value }) => (
            <div className="col-3" key={title}>
              <div className="small text-muted">{title}</div>
              <div className="small">{value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TotalRow = ({ label, value, type = "neutral" }) => {
  const cls =
    type === "credit" ? "text-success" : type === "debit" ? "text-danger" : "";

  return (
    <div className="d-flex justify-content-between">
      <span className="small text-muted">{label}</span>
      <span className={`small fw-semibold ${cls}`}>{value}</span>
    </div>
  );
};

export const OwnerCollectionTrList = ({ transactionId, onSummary }) => {
  const [officeSummary, setOfficeSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const fetchOfficeSummary = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const res = await api.get("v1/office/summary/after-ownercoll-summary", {
        transactionId,
      });
      if (onSummary) onSummary(res?.summary);
      setOfficeSummary(res?.summary || null);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => {
    fetchOfficeSummary();
  }, [fetchOfficeSummary]);

  if (loading)
    return (
      <div className="mb-3 p-2 border rounded bg-light">Loading summary…</div>
    );

  if (error)
    return (
      <div className="mb-3 p-2 border rounded bg-light text-danger">
        Error: {error}
      </div>
    );

  if (!officeSummary) return null;

  const totals = {
    pickup: officeSummary.overall?.totalPickups || 0,
    delivery: officeSummary.overall?.totalDeliveries || 0,

    totalCOD: officeSummary.overall?.totalCOD || 0,

    totalCommission: officeSummary.overall?.totalCommissionEarned || 0,
    totalUserBalanceDue: officeSummary.overall?.closingUserBalanceDue || 0,

    cashByFE: officeSummary.overall?.cashDepositsByFE || 0,
    onlineByFE: officeSummary.overall?.onlineDepositsByFE || 0,

    paidToFE: officeSummary.overall?.totalPaidToFE || 0,

    officeExpenses: officeSummary.officeExp?.totalOfficeExpenses || 0,

    prevOfficeBalance:
      officeSummary.overall?.openingOfficeBalance?.totalAvailable || 0,

    closingOfficeBalance:
      officeSummary.overall?.closingOfficeBalance?.totalAvailable || 0,

    totalRtoLoss: officeSummary.overall?.totalRtoLoss || 0,
  };

  totals.totalPaidByFE = totals.cashByFE + totals.onlineByFE;

  totals.remainingFromFE =
    totals.totalCOD - totals.totalPaidByFE - totals.paidToFE;
  totals.remainingCod = totals.totalCOD - totals.totalPaidByFE;

  totals.calculatedOfficeBalance =
    totals.prevOfficeBalance +
    totals.totalPaidByFE -
    totals.paidToFE -
    totals.officeExpenses;

  return (
    <div className="mb-3 p-2 border rounded bg-light">
      <div className="fw-bold mb-1">
        Summary{" "}
        <span style={{ fontSize: 10 }}>
          ({fmtDate(officeSummary.range?.[0])} –{" "}
          {fmtDate(officeSummary.range?.[1])})
        </span>
      </div>

      {/* -------- agent cards (UNCHANGED) -------- */}

      {officeSummary?.agents
        ?.filter(
          (x) =>
            x.totalPickups ||
            x.totalDeliveries ||
            x.totalCOD ||
            x.depositsByFeCash ||
            x.depositsByFeOnline ||
            x.depositsByFe ||
            x.paidToFe ||
            x.lastUserBalanceDue
        )
        .map((data = {}) => (
          <TrCard
            key={data._id || data.agentId}
            style={{ whiteSpace: "nowrap" }}
            data={{
              title: <span className="fw-bold">{getFullName(data.agent)}</span>,
              pickup: data.totalPickups,
              delivery: data.totalDeliveries,
              cols: [
                { title: "Total COD", value: INR(data.totalCOD) },
                {
                  title: "Total Paid",
                  value: (
                    <span className="fw-bold">{INR(data.depositsByFe)}</span>
                  ),
                },
                {
                  title: "Remaining",
                  value: (
                    <span className="">
                      {INR(data.totalCOD - data.depositsByFe)}
                    </span>
                  ),
                },
                {
                  title: "Office To FE",
                  value: INR(data.paidToFe),
                },

                {
                  title: "Cash Paid",
                  value: <span className="">{INR(data.depositsByFeCash)}</span>,
                },
                {
                  title: "Online Paid",
                  value: (
                    <span className="">{INR(data.depositsByFeOnline)}</span>
                  ),
                },
                {
                  title: "Comm Balance",
                  value: (
                    <span className="">{INR(data.lastCommissionBalance)}</span>
                  ),
                },
                {
                  title: "User Balance Due",
                  value: INR(data.lastUserBalanceDue),
                },
              ],
            }}
          />
        ))}

      {/* -------- TOTAL SUMMARY (REPLACED) -------- */}

      <div
        className="card mb-2"
        style={{ fontSize: 12, border: "1px solid #0d6efd" }}
      >
        <div className="card-body p-2">
          <div className="row mb-1">
            <div className="col-6 fw-bold">Total</div>
          </div>

          <div className="row gx-2 gy-1">
            {/* LEFT: OPERATIONS & EARNINGS */}
            <div className="col-6">
              <TotalRow
                label="Total COD"
                value={INR(totals.totalCOD)}
                type="credit"
              />
              <TotalRow
                label="Total Paid by FE"
                value={INR(totals.totalPaidByFE)}
                type="credit"
              />
              <TotalRow
                label={`${INR(totals.totalCOD)} - ${INR(
                  totals.totalPaidByFE
                )} = `}
                value={INR(totals.remainingCod)}
                type={totals.remainingCod >= 0 ? "debit" : "credit"}
              />

              <hr className="my-1" />

              <TotalRow
                label="Cash Paid by FE"
                value={INR(totals.cashByFE)}
                type="credit"
              />
              <TotalRow
                label="Online Paid by FE"
                value={INR(totals.onlineByFE)}
                type="credit"
              />

              <hr className="my-1" />

              <TotalRow label="Total Pickups" value={totals.pickup} />
              <TotalRow label="Total Deliveries" value={totals.delivery} />
              <TotalRow
                label="Total Commission"
                value={INR(totals.totalCommission)}
                type="debit"
              />

              <hr className="my-1" />

              <TotalRow label="RTO Loss" value={INR(totals.totalRtoLoss)} />
            </div>

            {/* RIGHT: OFFICE FLOW */}
            <div className="col-6">
              <TotalRow
                label="Previous office balance"
                value={INR(totals.prevOfficeBalance)}
                type={totals.prevOfficeBalance >= 0 ? "credit" : "debit"}
              />
              <TotalRow
                label="+ Total Paid by FE"
                value={INR(totals.totalPaidByFE)}
                type="credit"
              />
              <TotalRow
                label="- Office to FE"
                value={INR(totals.paidToFE)}
                type="debit"
              />

              <TotalRow
                label="- Office Expenses"
                value={INR(totals.officeExpenses)}
                type="debit"
              />

              <TotalRow
                label="= Calculated Office Balance"
                value={INR(totals.calculatedOfficeBalance)}
                type={totals.calculatedOfficeBalance >= 0 ? "credit" : "debit"}
              />

              <hr className="my-1" />

              <TotalRow
                label="System Office Balance"
                value={INR(totals.closingOfficeBalance)}
                type="credit"
              />
              <TotalRow
                label="Total User Balance Due"
                value={INR(totals.totalUserBalanceDue)}
                type={totals.totalUserBalanceDue >= 0 ? "debit" : "credit"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
